import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  LoadingContext,
  M_StateActions,
  MHeader,
  MButton,
  NavigateContext
} from 'src/M_Components';

import { BufferService } from 'src/M_Components/M_Utils/buffer.service';
import { isFailurePayload, apiMessage } from 'src/components/utils/apiResponse';
import DynamicForm from 'src/components/Forms/DynamicForm';
import '../pages.css';

/* -------------------------------------------------------------------------------------------------
 * toCamel
 * Lower-camelCase helper for DTO keys derived from Options metadata (Name/Header).
 * ------------------------------------------------------------------------------------------------- */
const toCamel = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/* -------------------------------------------------------------------------------------------------
 * isBoolean
 * Option type guard to coerce values safely for the payload.
 * ------------------------------------------------------------------------------------------------- */
const isBoolean = (opt) => opt?.Type === 'boolean' || opt?.Type === 'bool';

export default function CurrencyConfigCrud({ handleTabClose, toastMessages }) {
  const dispatch = useDispatch();
  const M_State = useSelector((state) => state.StateKeyValue);
  const selectedTab = M_State.find(o => o.key === 'selectedTab')?.value;

  const { t, i18n } = useTranslation();
  const ctx = useContext(LoadingContext);
  const { renameTab } = useContext(NavigateContext);

  // One-shot globals provided on open
  const globalOperation = M_State.find(obj => obj.key === 'operation')?.value;
  const globalLine = M_State.find(obj => obj.key === 'CurrencyLine')?.value;

  // Basic page state (tab-scoped)
  const [id, setId] = useState(M_State.find(obj => obj.key === 'selectedTab')?.value);
  const [operation, setOperation] = useState(globalOperation ?? 'Error');
  const [sessionLine, setSessionLine] = useState(globalLine ?? {});
  const [currencyInfo, setCurrencyInfo] = useState({});
  const [isFirst, setIsFirst] = useState(true);

  const isViewMode = (operation || '').toLowerCase() === 'view';

  // ------------------------- DynamicForm bits -------------------------
  const [schema, setSchema] = useState([]);     // raw Options from Currencies/Options
  const formRef = useRef(null);                 // imperative API (submit/getValues/reset)

  // ------------------------- Services (memoized) -------------------------
  const svcCurrencies = useMemo(() => new BufferService('Currencies', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
  const svcCreate = useMemo(() => new BufferService('Currencies/create', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
  const svcUpdate = useMemo(() => new BufferService('Currencies/update', ctx.DEFAULTURL), [ctx.DEFAULTURL]);

  /* ============================================================================================================
   * Tab label updates on operation/name/language changes
   * ========================================================================================================== */
  useEffect(() => {
    if (!selectedTab || !operation) return;
    const key =
      operation === 'Create' ? 'LabelNewCurrency'
        : operation === 'Edit' ? 'LabelEditCurrency'
          : 'LabelViewCurrency';
    const name = sessionLine?.Name ?? currencyInfo?.Name ?? '';
    const base = t(key);
    const label = (operation === 'Create' || !name) ? base : `${base} "${name}"`;
    renameTab(String(selectedTab), label);
  }, [selectedTab, operation, sessionLine?.Name, currencyInfo?.Name]);

  /* ============================================================================================================
   * Mount: restore tab snapshot; consume globals; bootstrap
   * ========================================================================================================== */
  useEffect(() => {
    const sessionForThisTab = M_State.find(o => o.key === id)?.value || [];
    const pick = (k) => sessionForThisTab.find(x => x.key === k)?.value;

    const snapInfo = pick('currencyInfo');
    const snapIF = pick('isFirst');
    const snapOP = pick('operation');
    const snapSL = pick('sessionLine');
    const snapSchema = pick('df.schema');

    if (snapInfo) setCurrencyInfo(snapInfo);
    if (snapSchema) setSchema(snapSchema);
    if (snapIF !== undefined) setIsFirst(snapIF); else if (isFirst) setIsFirst(false);

    // Prefer one-shot globals; fallback to snapshot
    const seedOperation = (globalOperation !== undefined ? globalOperation : snapOP);
    const seedLine = (globalLine !== undefined ? globalLine : snapSL);

    if (seedOperation !== undefined) setOperation(seedOperation);
    if (seedLine) setSessionLine(seedLine);

    // Consume globals so parallel tabs are independent
    if (globalOperation !== undefined) dispatch(M_StateActions.removeM_State({ key: 'operation' }));
    if (globalLine !== undefined) dispatch(M_StateActions.removeM_State({ key: 'CurrencyLine' }));

    bootstrap(seedOperation ?? operation, seedLine ?? sessionLine);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ============================================================================================================
   * Persist tab snapshot (also keeps unsaved edits when switching tabs)
   * ========================================================================================================== */
  useEffect(() => {
    if (!id) return;
    const sessionData = [
      { key: 'currencyInfo', value: currencyInfo },
      { key: 'isFirst', value: isFirst },
      { key: 'operation', value: operation },
      { key: 'sessionLine', value: sessionLine },
      { key: 'df.schema', value: schema },
    ];
    dispatch(M_StateActions.addM_State({ key: id, value: sessionData }));
  }, [id, currencyInfo, isFirst, operation, sessionLine, schema, dispatch]);

  /* ============================================================================================================
   * React to selectedTab change and restore that snapshot
   * ========================================================================================================== */
  useEffect(() => {
    const newId = M_State.find(obj => obj.key === 'selectedTab')?.value;
    if (newId !== id) {
      setId(newId);
      getSessionStorageData(newId);
    }
  }, [M_State]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ============================================================================================================
   * Refresh Options translations on language change
   * ========================================================================================================== */
  useEffect(() => {
    if (!operation || isFirst) return; // skip first render or no operation set
    fetchOptions(operation, sessionLine, { skipDetails: true });

    const key =
      operation === 'Create' ? 'LabelNewCurrency'
        : operation === 'Edit' ? 'LabelEditCurrency'
          : 'LabelViewCurrency';
    const name = sessionLine?.Name ?? currencyInfo?.Name ?? '';
    const base = t(key);
    const label = (operation === 'Create' || !name) ? base : `${base} "${name}"`;
    renameTab(String(selectedTab), label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // ------------------------------------------------------------------------------------------------------------
  // Restore another tab snapshot or re-bootstrap
  // ------------------------------------------------------------------------------------------------------------
  const getSessionStorageData = (sessionID) => {
    ctx.setIsLoad(true);
    const M_StateData = M_State.find(obj => obj.key === sessionID)?.value;
    if (M_StateData) {
      const map = {};
      M_StateData.forEach(o => { map[o.key] = o.value; });
      setCurrencyInfo(map['currencyInfo'] || {});
      setIsFirst(map['isFirst'] ?? false);
      setOperation(map['operation']);
      setSessionLine(map['sessionLine']);
      if (map['df.schema']) setSchema(map['df.schema']);
      ctx.setIsLoad(false);
      return;
    }
    bootstrap(operation, sessionLine);
  };

  /* ============================================================================================================
   * bootstrap: fetch Options, then details if not Create
   * ========================================================================================================== */
  const bootstrap = async (op, line) => {
    ctx.setIsLoad(true);
    try {
      await fetchOptions();
      if ((op || '').toLowerCase() !== 'create' && (line?.Id || sessionLine?.Id)) {
        await fetchDetails(line?.Id || sessionLine?.Id);
      }
    } finally {
      ctx.setIsLoad(false);
    }
  };

  /* ============================================================================================================
   * fetchOptions: load Currencies/Options → DynamicForm schema
   * ========================================================================================================== */
  const fetchOptions = async () => {
    try {
      const res = await svcCurrencies.options();
      if (isFailurePayload(res)) {
        toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
        return;
      }
      setSchema(Array.isArray(res) ? res : []);
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    }
  };

  /* ============================================================================================================
   * fetchDetails: GET currency by Id; Dynamic Form will hydrate/mirror
   * ========================================================================================================== */
  const fetchDetails = async (idToGet) => {
    try {
      const response = await svcCurrencies.getId(idToGet);
      if (isFailurePayload(response)) {
        toastMessages('error', '', apiMessage(response, t('ErrorLoadingData')));
        return;
      }
      setCurrencyInfo(response || {});
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    }
  };

  /* ============================================================================================================
   * getRefresh: re-fetch Options and details (if applicable)
   * ========================================================================================================== */
  const getRefresh = (line) => {
    const effectiveLine = line || sessionLine;
    if (operation !== 'Create' && !effectiveLine?.Id) {
      ctx.setIsLoad(false);
      return;
    }
    ctx.setIsLoad(true);
    Promise.all([
      fetchOptions(),
      (operation !== 'Create' ? fetchDetails(effectiveLine?.Id) : Promise.resolve())
    ]).finally(() => ctx.setIsLoad(false));
  };

  /* ============================================================================================================
   * handleFormValuesChange: merge Dynamic Form patches into tab state (keeps unsaved edits)
   * ========================================================================================================== */
  const handleFormValuesChange = useCallback((patch) => {
    if (!patch) return;
    setCurrencyInfo(prev => ({ ...prev, ...patch }));
  }, []);

  /* ============================================================================================================
   * buildPayloadFromValues:
   * - Iterate Options to choose keys and coercions.
   * - Use Header (preferably) or Name as the source key.
   * - Lower-camelCase for DTO keys.
   * - Coerce booleans.
   * Mandatory (business): Code, Name, Symbol, Accounting, Community.
   * ========================================================================================================== */
  const buildPayloadFromValues = (values) => {
    const opts = Array.isArray(schema) ? schema : [];
    const entries = [];

    for (const opt of opts) {
      if (!opt) continue;

      const formKey = opt.Name || opt.Header; // no FKs expected here, but this is safe
      if (!formKey) continue;

      const payloadKey = toCamel(opt.Header || opt.Name);
      let v = values?.[formKey];

      if (isBoolean(opt)) v = !!v;

      if (v !== undefined) entries.push([payloadKey, v]);
    }

    return Object.fromEntries(entries);
  };

  /* ============================================================================================================
   * onSubmitDynamic: create/update based on operation
   * ========================================================================================================== */
  const onSubmitDynamic = useCallback(async (values) => {
    ctx.setIsLoad(true);
    try {
      const payload = buildPayloadFromValues(values);

      // Business validation mirrored from legacy page
      if (!payload.code || !payload.name || !payload.symbol || !payload.accounting || !payload.community) {
        toastMessages('error', '', t('MandatoryFields'));
        return;
      }

      const isCreate = (operation || '').toLowerCase() === 'create';
      const res = isCreate
        ? await svcCreate.post(payload)
        : await svcUpdate.update(sessionLine?.Id, payload);

      if (isFailurePayload(res)) {
        toastMessages('error', '', apiMessage(res, t('ErrorSaving')));
        return;
      }

      toastMessages('success', '', isCreate ? t('CurrencyCreated') : t('CurrencyUpdated'));
      handleTabClose(id);
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorSaving')));
    } finally {
      ctx.setIsLoad(false);
    }
  }, [operation, svcCreate, svcUpdate, sessionLine?.Id, ctx, id, t, toastMessages, schema]);

  /* ============================================================================================================
   * triggerFormSubmit: call Dynamic Form submit via imperative ref
   * ========================================================================================================== */
  const triggerFormSubmit = () => {
    formRef.current?.submit?.();
  };

  return (
    <>
      <MHeader element={
        <>
          <MButton
            tooltip={t('RefreshTable')}
            icon={<i className='pi pi-refresh iconTable'></i>}
            onClick={() => getRefresh(sessionLine)}
            label={t('RefreshTable')}
          />
          {!isViewMode && (
            <MButton
              tooltip={t('Save')}
              icon={<i className='pi pi-save iconTable'></i>}
              onClick={triggerFormSubmit}
              label={t('Save')}
            />
          )}
        </>
      } />

      <div style={{
        padding: '20px',
        background: 'var(--color-white)'
      }}>
        <div className='firstDivContainer'

        >
          <DynamicForm
            // No overrides: Dynamic Form already sorts & includes per mode; just feed options and data.
            key={`df-currencies-${selectedTab}-${operation}-${sessionLine?.Id ?? 'new'}`}
            formRef={formRef}
            mode={(operation || 'View').toLowerCase()}
            schema={schema}
            data={currencyInfo}
            onValuesChange={handleFormValuesChange}
            onSubmit={onSubmitDynamic}
            title={t('Currency Information')}
            containerClassName="alignInputsUserForm"
          />
        </div>
      </div>
    </>
  );
}

CurrencyConfigCrud.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func
};
